// @flow
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { fetchSimilarItemsV3 } from '../api/similarItems';
import { getAuthToken } from './user';
import { getBiddingInfo } from './biddingInfo';
import { getCatalogRegistration } from './catalogRegistration';
import { getDeployment } from './config';
import { getLiveItemStatus } from './liveItemStatus';
import { getUserExcludedHouses } from './searchExclusions';
import type { GlobalState } from '../rootReducer';
import ms from 'ms';
import { type SimilarItemInfo as SimilarItemInfoType } from '../../types/SimilarItemInfo';
import { transformSimilarItemsFromV3 } from '../../utils/similarItems';
import union from 'lodash/union';

/* Action Types */
export const LOAD_SIMILAR_ITEMS_V3_FAIL = 'la/domain/similarItemsV3/LOAD_FAIL';
export const LOAD_SIMILAR_ITEMS_V3_REQUEST = 'la/domain/similarItemsV3/LOAD_REQUEST';
export const LOAD_SIMILAR_ITEMS_V3_SUCCESS = 'la/domain/similarItemsV3/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SIMILAR_ITEMS_V3_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.itemId]),
            };
        case LOAD_SIMILAR_ITEMS_V3_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_SIMILAR_ITEMS_V3_SUCCESS:
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [action.meta.itemId]: action.payload,
                },
                loaded: {
                    ...state.loaded,
                    [action.meta.itemId]: action.meta.actionTime,
                },
                loading: difference(state.loading, [action.meta.itemId]),
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.similarItemsV3;
const globalStateSelector = (state: GlobalState) => state;
const idSelector = (state, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getSimilarItemIds = createSelector([byIdSelector, idSelector], (byId, id) =>
    Array.isArray(byId[id]) ? byId[id].map(item => item.itemId) : [],
);

const getSimilarItems = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || []);

export const getSimilarItemsV3 = createSelector([globalStateSelector, idSelector], (state, itemId, limit) => {
    let similarItems = getSimilarItems(state, itemId);
    similarItems = similarItems.filter(similarItem => similarItem.item.itemId !== itemId);
    similarItems = similarItems.slice(0, limit);
    const loadedCardDatas = similarItems.map(similarItem => {
        if (similarItem.catalog?.catalogId) {
            similarItem.catalogRegistration = getCatalogRegistration(state, similarItem.catalog.catalogId);
        }
        if (similarItem.item.itemId) {
            similarItem.biddingInfo = getBiddingInfo(state, similarItem.item.itemId);
            similarItem.liveItemStatus =
                similarItem.catalog?.catalogStatus === 'live' ? getLiveItemStatus(state, similarItem.item.itemId) : {};
        }
        return similarItem;
    });
    return loadedCardDatas;
});

export const getLoadTime = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

export const isLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetch = (state, itemId) => {
    if (!itemId) return false;
    const item = getSimilarItemIds(state, itemId);
    if (item.length) {
        const loaded = getLoadTime(state, itemId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    const loading = isLoading(state, itemId);
    return !loading;
};

/* ACTION CREATORS */
const loadSimilarItemsV3 = (similarItemInfo: SimilarItemInfoType) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const searchExclusions = getUserExcludedHouses(state);

        dispatch({
            payload: similarItemInfo.itemId,
            type: LOAD_SIMILAR_ITEMS_V3_REQUEST,
        });

        const response = await fetchSimilarItemsV3({ authToken, deployment, searchExclusions, similarItemInfo });
        const transformedItems = transformSimilarItemsFromV3(response.payload.items);
        return dispatch({
            meta: { actionTime: Date.now(), itemId: similarItemInfo.itemId },
            payload: transformedItems,
            type: LOAD_SIMILAR_ITEMS_V3_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            meta: { itemId: similarItemInfo.itemId },
            payload: error,
            type: LOAD_SIMILAR_ITEMS_V3_FAIL,
        });
    }
};

export const fetchSimilarItemsV3IfNeeded = (similarItemInfo: SimilarItemInfoType) => async (
    dispatch: Function,
    getState: Function,
) => {
    if (similarItemInfo?.itemId && shouldFetch(getState(), similarItemInfo.itemId)) {
        return dispatch(loadSimilarItemsV3(similarItemInfo));
    }
    return Promise.resolve();
};
